import { QueryHookOptions } from '@apollo/client';
import { useApiMutation, useApiQuery } from '@common/hooks';

import { ActivateProviderResponse } from './types/ActivateProviderResponse.types';
import { ActivateProviderVariables } from './types/ActivateProviderVariables.types';
import { DeactivateProviderResponse } from './types/DeactivateProviderResponse.types';
import { DeactivateProviderVariables } from './types/DeactivateProviderVariables.types';
import {
  activateProvider,
  deactivateProvider,
  getProviderById,
  getProviderFilters,
  getProvidersList,
  updateProvider
} from './services';
import {
  GetProviderByIdResponse,
  GetProviderByIdVariables,
  GetProviderFiltersResponse,
  GetProvidersListResponse,
  GetProvidersListVariables,
  UpdateProviderResponse,
  UpdateProviderVariables
} from './types';

const useGetProviders = (
  options?: QueryHookOptions<
    GetProvidersListResponse,
    GetProvidersListVariables
  >
) =>
  useApiQuery<GetProvidersListResponse, GetProvidersListVariables>(
    getProvidersList,
    'getProvidersList',
    {
      ...options,
      fetchPolicy: 'cache-and-network'
    },
    {
      withLoader: false
    }
  );

const useGetProviderFilters = () =>
  useApiQuery<GetProviderFiltersResponse>(
    getProviderFilters,
    'getProviderFilters',
    {
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true // important!
    }
  );

const useGetProviderByIdDetails = (
  options?: QueryHookOptions<GetProviderByIdResponse, GetProviderByIdVariables>
) =>
  useApiQuery<GetProviderByIdResponse, GetProviderByIdVariables>(
    getProviderById,
    'getProviderById',
    {
      ...options,
      fetchPolicy: 'network-only'
    }
  );

const useActivateProvider = (providerId: string) =>
  useApiMutation<ActivateProviderResponse, ActivateProviderVariables>(
    activateProvider,
    'activateProvider',
    {
      buttonLoader: true,
      successMsg: 'Provider was activated successfully.',
      withLoader: false
    },
    {
      refetchQueries: [
        {
          query: getProviderById,
          variables: {
            providerId
          }
        }
      ],
      awaitRefetchQueries: true
    }
  );

const useDeactivateProvider = (providerId: string) =>
  useApiMutation<DeactivateProviderResponse, DeactivateProviderVariables>(
    deactivateProvider,
    'deactivateProvider',
    {
      buttonLoader: true,
      successMsg: 'Provider was deactivated successfully.',
      withLoader: false
    },
    {
      errorPolicy: 'all',
      refetchQueries: [
        {
          query: getProviderById,
          variables: {
            providerId
          }
        }
      ],
      awaitRefetchQueries: true
    }
  );

const useUpdateProvider = (providerId: string) =>
  useApiMutation<UpdateProviderResponse, UpdateProviderVariables>(
    updateProvider,
    'updateProvider',
    {
      buttonLoader: true,
      successMsg: 'Provider was updated successfully',
      withLoader: false
    },
    {
      refetchQueries: [
        {
          query: getProviderById,
          variables: {
            providerId
          }
        }
      ],
      awaitRefetchQueries: true
    }
  );

export {
  useActivateProvider,
  useDeactivateProvider,
  useGetProviderByIdDetails,
  useGetProviderFilters,
  useGetProviders,
  useUpdateProvider
};
